import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { Card } from "@magnetic/card";
import { ArrowSquareOut, CopySimple } from "@magnetic/icons";
import { Modal } from "@magnetic/modal";
import { Text } from "@magnetic/text";

import { Button } from "src/components/buttons";
import { EmptyContainer } from "src/components/emptyContainer";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { PATHS } from "src/core/constants";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  CliCommandCompletion,
  CliCommandResponse,
  CommandAction
} from "src/gen/schema/bilge/api";
import { NodeType } from "src/gen/schema/models/fabric";

import { MockShellHelp } from "./mockShellHelp";
import { MockShellHistoryList } from "./mockShellHistoryList";
import { MockShellInput } from "./mockShellInput";
import { MockShellItemHeader } from "./mockShellItemHeader";
import { CommandHeader, MockShellOutput } from "./mockShellOutput";
import {
  addCommandOutput,
  CommandHistory,
  createInitialHistory,
  MAX_OUTPUT_LINES
} from "./mockShellSessionManager";
import { CommandExecution } from "./mockShellTypes";
import {
  copyMockshellCmds,
  getDevice,
  getInitialCommand
} from "./mockShellUtils";

import "./manageMockShellModal.scss";

interface MockshellProps {
  readonly isPopOut?: boolean;
  readonly onClose: () => void;
  readonly initialCommandOverride?: string;
}
export const ManageMockShellModal = (props: MockshellProps) => {
  const { onClose, initialCommandOverride } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | undefined>();
  const [cmdResult, setCmdResult] = useState<CliCommandResponse | undefined>();
  const [action, setAction] = useState<CommandAction>();
  const [suggestions, setSuggestions] = useState<CliCommandCompletion[]>([]);
  const [isSuggestionError, setIsSuggestionError] = useState<boolean>(false);
  const [longestMatchToAutoComplete, setLongestMatchToAutoComplete] =
    useState<string>("");
  const { fabric } = useContext(DataProviderContext);
  const location = useLocation();
  const initialCommand = useMemo(() => {
    // Prioritize override from URL params
    if (initialCommandOverride) {
      return initialCommandOverride;
    }
    return getInitialCommand(location.pathname, fabric);
  }, [fabric, location.pathname, initialCommandOverride]);
  const [command, setCommand] = useState(initialCommand);
  const [cursorPos, setCursorPos] = useState<number>(command.length);
  const [previousCommand, setPreviousCommand] = useState<string>("");
  const [executedAt, setExecutedAt] = useState<Date | undefined>();
  const [commandHistory, setCommandHistory] = useState<CommandHistory>(
    createInitialHistory()
  );
  const isMockShellLiveToolsEnabled =
    window.cnc.settings.get("mockshellLiveTools");
  const outputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMockShellLiveToolsEnabled && outputContainerRef.current) {
      // Scroll to the last child (latest command output) with smooth animation
      const lastChild = outputContainerRef.current.lastElementChild;
      if (lastChild) {
        lastChild.scrollIntoView({
          behavior: "smooth",
          block: "end"
        });
      }
    }
  }, [commandHistory, cmdResult, isMockShellLiveToolsEnabled]);

  const popOutUrl = useMemo(() => {
    if (!fabric?.id) return "";
    const baseUrl = `/${PATHS.FABRIC}/${fabric.id}/${PATHS.MOCK_SHELL}`;
    // Include current command if present
    if (command && command !== initialCommand) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set("command", command);
      return url.pathname + url.search; // Return relative path with query
    }
    return baseUrl;
  }, [fabric?.id, command, initialCommand]);

  const handlePopOut = useCallback(() => {
    if (!!popOutUrl) {
      window.open(popOutUrl, "_blank");
      onClose();
    }
  }, [popOutUrl, onClose]);

  // If device is blueprint node, then disable mockshell Input and show a message
  const isBlueprintNode = useMemo(() => {
    const device = getDevice(location.pathname, fabric);
    // If no device found (not on device page), allow it
    if (!device) {
      return false;
    }
    return device.nodeType === NodeType.NODE_BLUEPRINT;
  }, [location.pathname, fabric]);

  const commandExecution = useCallback(
    ({ cmd, cmdAction, resultFormat, cmdPos }: CommandExecution) => {
      if (fabric) {
        const payload = {
          command: cmd,
          action: cmdAction,
          fabricId: fabric.id,
          resultFormat,
          cursorPos: cmdPos,
          cmdParams: [],
          initialCommand,
          deviceId: ""
        };
        setLoading(true);
        Bilge.tCliCommand(payload)
          .then((response) => {
            setCmdResult(response);
            switch (cmdAction) {
              case CommandAction.EXECUTE:
                if (response.command) {
                  // set the initial command returned by backend, if command action execute was successful
                  setCommand(response.command);
                  setCursorPos(response.cursorPos);
                  setPreviousCommand(cmd);
                  setExecutedAt(new Date()); // Capture execution timestamp

                  setCommandHistory((prev) =>
                    addCommandOutput(
                      prev,
                      cmd,
                      response.executionResults,
                      response.resultFormat
                    )
                  );
                }
                break;
              case CommandAction.EXPAND:
                // expand should auto complete a command if possible and also provide the list of next available  commands
                setCommand(response.command);
                setCursorPos(response.cursorPos);
                break;
              default:
                break;
            }
          })
          .catch((err) => {
            setError(err);
            // Clear the previous command
            setPreviousCommand(cmd);
            setExecutedAt(new Date()); // Capture execution timestamp for failed commands
            setCmdResult(undefined);
          })
          .finally(() => {
            setAction(cmdAction);
            setLoading(false);
          });
      }
    },
    [fabric, initialCommand]
  );

  const completeCommand = useCallback(
    ({ cmd, resultFormat, cmdPos }: CommandExecution) => {
      if (fabric) {
        const payload = {
          command: cmd,
          action: CommandAction.COMPLETE,
          fabricId: fabric.id,
          resultFormat,
          cursorPos: cmdPos,
          cmdParams: [],
          initialCommand,
          deviceId: ""
        };
        setLoading(true);
        Bilge.tCliCommand(payload)
          .then((response) => {
            setCmdResult(response);
            setCommand(response.command);
            setCursorPos(response.cursorPos);
          })
          .catch((err) => {
            setError(err);
            setIsSuggestionError(true);
            setCmdResult(undefined);
          })
          .finally(() => {
            setAction(CommandAction.COMPLETE);
            setLoading(false);
          });
      }
    },
    [fabric, initialCommand]
  );
  const clearError = useCallback(() => setError(undefined), []);
  const handleCopy = useCallback(() => {
    copyMockshellCmds(outputContainerRef);
  }, []);

  const showHelpOutput =
    (action === CommandAction.COMPLETE ||
      action === CommandAction.EXPOUND ||
      action === CommandAction.EXPAND) &&
    cmdResult;
  return (
    <Modal isOpen={true} size={"xl"} className="cnc-manage-mock-shell">
      <HFlex justify="flex-end">
        <MockShellHelp />
        {isMockShellLiveToolsEnabled && commandHistory.commands.length > 0 && (
          <Button
            name="copy-all-session"
            kind="tertiary"
            size="sm"
            icon={<CopySimple />}
            onClick={handleCopy}
            data-qa="mock-shell-copy-all"
            aria-label="Copy all session"
          >
            Copy session
          </Button>
        )}
        {isMockShellLiveToolsEnabled && (
          <Button
            name="pop-out-mock-shell"
            kind="tertiary"
            size="md"
            onClick={handlePopOut}
            disabled={!popOutUrl}
            data-qa="mock-shell-pop-out-button"
            aria-label="Open in new window"
            icon={<ArrowSquareOut size={20} />}
          />
        )}
        <Button
          name="mock-shell-close"
          kind="tertiary"
          size="md"
          onClick={onClose}
        >
          Close
        </Button>
      </HFlex>
      <Modal.Divider size="xs" />
      <Card className="cnc-manage-mock-shell-card">
        <Card.Well
          direction="vertical"
          gap="md"
          className="cnc-manage-mock-shell-container"
        >
          {!isMockShellLiveToolsEnabled && (
            <VFlex>
              <MockShellInput
                completeCommand={completeCommand}
                commandExecution={commandExecution}
                commandCompleteSuggestions={suggestions}
                onCommandCompleteSuggestionsChange={setSuggestions}
                onClearError={setError}
                loading={loading}
                isSuggestionError={isSuggestionError}
                setIsSuggestionError={setIsSuggestionError}
                longestMatchToAutoComplete={longestMatchToAutoComplete}
                setLongestMatchToAutoComplete={setLongestMatchToAutoComplete}
                command={command}
                setCommand={setCommand}
                cursorPos={cursorPos}
                setCursorPos={setCursorPos}
                initialCommand={initialCommand}
                isBlueprintNode={isBlueprintNode}
              />
              {isBlueprintNode && (
                <EmptyContainer
                  text=""
                  additionalContent={
                    <VFlex
                      gap="sm"
                      align="center"
                      data-qa="quick-command-unavailable-message"
                    >
                      <Text weight="bold" size="p2">
                        Quick Command unavailable for unbound device
                      </Text>
                      <Text weight="light">
                        Bind device to start using Quick Command
                      </Text>
                    </VFlex>
                  }
                />
              )}
              {error && (
                <VFlex gap="xs">
                  <CommandHeader
                    command={previousCommand}
                    executedAt={executedAt}
                  />
                  <ErrorNotification error={error} onClose={clearError} />
                </VFlex>
              )}
              <MockShellOutput
                cmdResult={cmdResult}
                loading={loading}
                cmdAction={action}
                previousCommand={previousCommand}
                executedAt={executedAt}
              />
            </VFlex>
          )}
          {isMockShellLiveToolsEnabled && (
            <VFlex className="mock-shell-live-tools-container">
              <div
                className="mock-shell-output-history-container"
                ref={outputContainerRef}
              >
                {/* History list */}
                <MockShellHistoryList
                  history={commandHistory}
                  loading={loading}
                />

                {/* Help/Suggestions output from Tab key - NOT counted towards 1000 lines */}
                {showHelpOutput && (
                  <MockShellOutput
                    cmdResult={cmdResult}
                    loading={loading}
                    cmdAction={action}
                    previousCommand={previousCommand}
                    executedAt={executedAt}
                  />
                )}
                {isBlueprintNode && (
                  <EmptyContainer
                    text=""
                    additionalContent={
                      <VFlex
                        gap="sm"
                        align="center"
                        data-qa="quick-command-unavailable-message"
                      >
                        <Text weight="bold" size="p2">
                          Quick Command unavailable for unbound device
                        </Text>
                        <Text weight="light">
                          Bind device to start using Quick Command
                        </Text>
                      </VFlex>
                    }
                  />
                )}
                {error && (
                  <VFlex className="mock-shell-history-item">
                    <MockShellItemHeader
                      command={previousCommand}
                      executedAt={executedAt}
                      showCopyButton={false}
                      showPopOutButton={false}
                    />
                    <div>
                      <ErrorNotification error={error} onClose={clearError} />
                    </div>
                  </VFlex>
                )}
              </div>
              <VFlex className="mock-shell-input-section">
                <MockShellInput
                  completeCommand={completeCommand}
                  commandExecution={commandExecution}
                  commandCompleteSuggestions={suggestions}
                  onCommandCompleteSuggestionsChange={setSuggestions}
                  onClearError={setError}
                  loading={loading}
                  isSuggestionError={isSuggestionError}
                  setIsSuggestionError={setIsSuggestionError}
                  longestMatchToAutoComplete={longestMatchToAutoComplete}
                  setLongestMatchToAutoComplete={setLongestMatchToAutoComplete}
                  command={command}
                  setCommand={setCommand}
                  cursorPos={cursorPos}
                  setCursorPos={setCursorPos}
                  initialCommand={initialCommand}
                  isBlueprintNode={isBlueprintNode}
                />
                <Text size="p5" color="light" className="mock-shell-info-text">
                  Max scrollback: {MAX_OUTPUT_LINES} lines.
                </Text>
              </VFlex>
            </VFlex>
          )}
        </Card.Well>
      </Card>
    </Modal>
  );
};
